import React, { useEffect, useState } from 'react';
import { HdrPricingSlider } from '@rehub/pricing-ui';
import {
  HDR_CONTACT_EMAIL,
  HDR_PRICING_COPY,
  hdrPlanPriceMinor,
  type HdrCurrency,
  type HdrPlan,
} from '@rehub/pricing';
import {
  cancelHdrSubscription,
  getHdrPricingPlans,
  getMyHdrEntitlement,
  startHdrCheckout,
  type HdrEntitlement,
} from '../services/hdr';
import { trackHdrEvent } from '../services/analytics';
import HdrUsageCard from './HdrUsageCard';

export default function HdrPricingPage({
  entitlement: entitlementProp,
  signedIn = true,
}: {
  entitlement?: HdrEntitlement | null;
  signedIn?: boolean;
}) {
  const [plans, setPlans] = useState<HdrPlan[] | undefined>(undefined);
  const [entitlement, setEntitlement] = useState<HdrEntitlement | null>(entitlementProp ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getHdrPricingPlans().then(setPlans);
    if (signedIn) getMyHdrEntitlement().then(setEntitlement);
    trackHdrEvent('hdr_pricing_viewed', {});
  }, [signedIn]);

  const handleCta = async (plan: HdrPlan, currency: HdrCurrency) => {
    trackHdrEvent('hdr_plan_selected', {
      plan: plan.id,
      allowance: plan.imageAllowance,
      currency,
      price: hdrPlanPriceMinor(plan, currency) / 100,
    });
    if (plan.isContact) {
      window.location.href = `mailto:${HDR_CONTACT_EMAIL}?subject=HDRPhoto%205000%2B%20plan`;
      return;
    }
    if (!signedIn) {
      window.location.href = '/login?mode=signup';
      return;
    }
    if (plan.id === 'free') {
      window.location.href = '/upload';
      return;
    }
    setBusy(true);
    setError(null);
    try {
      trackHdrEvent('hdr_checkout_started', {
        plan: plan.id,
        allowance: plan.imageAllowance,
        currency,
        price: hdrPlanPriceMinor(plan, currency) / 100,
      });
      const result = await startHdrCheckout(plan.id, currency);
      if (result?.url) {
        window.location.href = result.url;
        return;
      }
      if (result?.updated) {
        const previous = entitlement?.plan_id;
        const event = previous && plan.displayOrder < (plans.find((p) => p.id === previous)?.displayOrder || 0)
          ? 'hdr_subscription_downgraded'
          : 'hdr_subscription_upgraded';
        trackHdrEvent(event, { plan: plan.id, currency, allowance: plan.imageAllowance });
        const next = await getMyHdrEntitlement();
        setEntitlement(next);
      }
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel your HDRPhoto plan at the end of this billing period?')) return;
    setBusy(true);
    try {
      await cancelHdrSubscription();
      trackHdrEvent('hdr_subscription_cancelled', { plan: entitlement?.plan_id });
      setEntitlement(await getMyHdrEntitlement());
    } catch (err: any) {
      setError(err?.message || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6 lg:p-10">
      <h1 className="text-3xl font-bold text-white sm:text-4xl">{HDR_PRICING_COPY.heading}</h1>
      <p className="mt-3 max-w-2xl text-slate-400">{HDR_PRICING_COPY.supporting}</p>

      {entitlement && (
        <div className="mt-8">
          <HdrUsageCard entitlement={entitlement} />
        </div>
      )}

      <div className="mt-8">
        <HdrPricingSlider
          plans={plans}
          remaining={entitlement?.remaining ?? null}
          currentAllowance={entitlement?.allowance ?? null}
          onPlanChange={(plan, currency) =>
            trackHdrEvent('hdr_pricing_slider_changed', {
              plan: plan.id,
              allowance: plan.imageAllowance,
              currency,
            })
          }
          onCta={handleCta}
        />
      </div>

      {busy && <p className="mt-4 text-sm text-slate-400">Working…</p>}
      {error && <p className="mt-4 text-sm text-amber-300">{error}</p>}

      {entitlement?.is_paid && !entitlement.cancel_at_period_end && (
        <button
          type="button"
          onClick={handleCancel}
          className="mt-6 text-sm text-slate-500 hover:text-slate-300"
        >
          Cancel at period end
        </button>
      )}
    </div>
  );
}
